import type { AlertModalProps, ConfirmModalProps } from '@viewup/ui';
import type { WritableDraft } from 'immer';
import { createSelector } from 'viewup-lib/common';
import type { StoreApi, UseBoundStore } from 'zustand';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { GlobalModalStoreProps } from './useGlobalModalStore.types';

const defaultAlertModalProps: AlertModalProps = {
  actionButtonText: '',
  buttonText: '확인',
  children: undefined,
  container: undefined,
  content: '',
  defaultOpen: false,
  exitHandler: () => {},
  footer: undefined,
  hasCloseButton: false,
  hasIcon: true,
  iconColor: 'primary.400',
  iconSource: undefined,
  isOpen: false,
  modal: undefined,
  onClickActionButton: undefined,
  onClickButton: undefined,
  title: '',
};

const defaultConfirmModalProps: ConfirmModalProps = {
  ...defaultAlertModalProps,
  actionButtonText: '',
  fillButtonText: '확인',
  onClickActionButton: undefined,
  onClickFillButton: undefined,
  onClickOutlineButton: undefined,
  outlineButtonText: '취소',
};

export const useGlobalModalStoreBase = create<GlobalModalStoreProps>()(
  immer((set, get) => ({
    actions: {
      closeAlertModal: () => {
        set((prev) => {
          prev.alertModalProps.isOpen = false;
        });
      },
      closeConfirmModal: () => {
        set((prev) => {
          prev.confirmModalProps.isOpen = false;
        });
      },
      setAlertModal: (props) => {
        set((prev) => {
          prev.alertModalProps = {
            ...defaultAlertModalProps,
            ...props,
            exitHandler: () => {
              props?.exitHandler?.();
              get().actions.closeAlertModal();
            },
            isOpen: true,
            onClickActionButton: () => {
              props?.onClickActionButton?.();
              get().actions.closeAlertModal();
            },
            onClickButton: () => {
              props?.onClickButton?.();
              get().actions.closeAlertModal();
            },
          } as WritableDraft<AlertModalProps>;
        });
      },
      setConfirmModal: (props) => {
        set((prev) => {
          prev.confirmModalProps = {
            ...defaultConfirmModalProps,
            ...props,
            exitHandler: () => {
              props?.exitHandler?.();
              get().actions.closeConfirmModal();
            },
            isOpen: true,
            onClickActionButton: () => {
              props?.onClickActionButton?.();
              get().actions.closeConfirmModal();
            },
            onClickFillButton: () => {
              props?.onClickFillButton?.();
              get().actions.closeConfirmModal();
            },
            onClickOutlineButton: () => {
              props?.onClickOutlineButton?.();
              get().actions.closeConfirmModal();
            },
          } as WritableDraft<ConfirmModalProps>;
        });
      },
      setSyncConfirmModal: async (props) => {
        return new Promise((res) => {
          set((prev) => {
            prev.confirmModalProps = {
              ...defaultConfirmModalProps,
              ...props,
              exitHandler: () => {
                props?.exitHandler?.();
                get().actions.closeConfirmModal();
                res('exit');
              },
              isOpen: true,
              onClickFillButton: () => {
                props?.onClickFillButton?.();
                get().actions.closeConfirmModal();
                res('fill');
              },
              onClickOutlineButton: () => {
                props?.onClickOutlineButton?.();
                get().actions.closeConfirmModal();
                res('outline');
              },
            } as WritableDraft<ConfirmModalProps>;
          });
        });
      },
    },
    alertModalProps: {
      ...defaultAlertModalProps,
      exitHandler: () => {
        set((prev) => {
          prev.alertModalProps.isOpen = false;
        });
      },
    },
    confirmModalProps: {
      ...defaultConfirmModalProps,
      exitHandler: () => {
        set((prev) => {
          prev.confirmModalProps.isOpen = false;
        });
      },
    },
  })),
) as UseBoundStore<StoreApi<GlobalModalStoreProps>>;

export const useGlobalModalStore = createSelector<
  GlobalModalStoreProps,
  typeof useGlobalModalStoreBase
>(useGlobalModalStoreBase);
